"use client";

import * as React from "react";
import { scaleLinear } from "d3-scale";
import { line, area, curveMonotoneX } from "d3-shape";
import { bisector } from "d3-array";
import { useSize } from "../../hooks";

/**
 * Measured render-path comparison from Swarmhold.
 *
 * Same hardware, same camera protocol, 29–30 one-second samples per band.
 * `low` is the worst single second of the band, where it was captured.
 */
type Row = {
  enemies: number;
  skinned: number;
  skinnedLow?: number;
  instanced: number;
  instancedLow: number;
};

const DATA: Row[] = [
  { enemies: 200, skinned: 111, instanced: 117.3, instancedLow: 89 },
  { enemies: 400, skinned: 69, instanced: 117.5, instancedLow: 109 },
  {
    enemies: 600,
    skinned: 63,
    skinnedLow: 36,
    instanced: 89.8,
    instancedLow: 76,
  },
  {
    enemies: 800,
    skinned: 43,
    skinnedLow: 32,
    instanced: 87.1,
    instancedLow: 77,
  },
  {
    enemies: 1000,
    skinned: 52,
    skinnedLow: 27,
    instanced: 84.3,
    instancedLow: 79,
  },
];

const SERIES = [
  {
    key: "instanced" as const,
    lowKey: "instancedLow" as const,
    label: "GPU-instanced (VAT)",
    color: "var(--series-a)",
    /** Worst-second was captured at every band, so the band is honest. */
    band: true,
  },
  {
    key: "skinned" as const,
    lowKey: "skinnedLow" as const,
    label: "Skinned mesh + Animator",
    color: "var(--series-b)",
    // Only three of five bands recorded a minimum; a partial band would read
    // as a step change that never happened. The numbers live in the tooltip.
    band: false,
  },
];

const MARGIN = { top: 18, right: 18, bottom: 34, left: 40 };
const bisectEnemies = bisector<Row, number>((d) => d.enemies).center;

export default function PerfLadder() {
  const [wrapRef, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = React.useState<number | null>(null);
  const [shown, setShown] = React.useState<Record<string, boolean>>({
    instanced: true,
    skinned: true,
  });
  const [drawn, setDrawn] = React.useState(false);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setDrawn(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDrawn(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [wrapRef]);

  const w = Math.max(size.width, 280);
  const h = Math.min(Math.max(Math.round(w * 0.42), 230), 420);
  const innerW = w - MARGIN.left - MARGIN.right;
  const innerH = h - MARGIN.top - MARGIN.bottom;

  const x = React.useMemo(
    () => scaleLinear().domain([200, 1000]).range([0, innerW]),
    [innerW],
  );
  const y = React.useMemo(
    () => scaleLinear().domain([0, 130]).nice().range([innerH, 0]),
    [innerH],
  );

  const mkLine = React.useMemo(
    () =>
      line<Row>()
        .x((d) => x(d.enemies))
        .curve(curveMonotoneX),
    [x],
  );

  const mkBand = React.useMemo(
    () =>
      area<Row>()
        .x((d) => x(d.enemies))
        .curve(curveMonotoneX),
    [x],
  );

  const active = hover === null ? null : DATA[hover];

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const value = x.invert(e.clientX - rect.left);
    setHover(bisectEnemies(DATA, value));
  };

  return (
    <div className="panel overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b px-4 py-3">
        <h3 className="text-sm font-medium">
          Frame rate against enemy count, by render path
        </h3>
        <p className="font-mono text-2xs text-faint">
          measured · 2 peers · 29-30 samples/band
        </p>
      </div>

      <div
        ref={wrapRef}
        className="relative px-2 pt-2"
        style={
          {
            "--series-a": "rgb(var(--accent))",
            "--series-b": "rgb(var(--signal))",
          } as React.CSSProperties
        }
      >
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label="Line chart. GPU-instanced rendering holds 84 to 117 frames per second from 200 to 1000 enemies. Skinned-mesh rendering falls from 111 to the forties over the same range."
        >
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {/* y gridlines */}
            {y.ticks(5).map((t) => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line
                  x2={innerW}
                  stroke="rgb(var(--line) / var(--line-a))"
                  strokeDasharray={t === 0 ? undefined : "2 4"}
                />
                <text
                  x={-8}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-[rgb(var(--faint))] font-mono text-[10px]"
                >
                  {t}
                </text>
              </g>
            ))}

            {/* the only line that matters: 60 fps */}
            <g transform={`translate(0,${y(60)})`}>
              <line
                x2={innerW}
                stroke="rgb(var(--line) / 0.38)"
                strokeDasharray="5 3"
              />
              <text
                x={innerW}
                dy="-5"
                textAnchor="end"
                className="fill-[rgb(var(--muted))] font-mono text-[10px]"
              >
                60 fps target
              </text>
            </g>

            {/* x axis */}
            <g transform={`translate(0,${innerH})`}>
              <line
                x2={innerW}
                stroke="rgb(var(--line) / var(--line-a-strong))"
              />
              {DATA.map((d) => (
                <text
                  key={d.enemies}
                  x={x(d.enemies)}
                  y={18}
                  textAnchor="middle"
                  className="fill-[rgb(var(--faint))] font-mono text-[10px]"
                >
                  {d.enemies}
                </text>
              ))}
              <text
                x={innerW / 2}
                y={32}
                textAnchor="middle"
                className="fill-[rgb(var(--faint))] font-mono text-[10px]"
              >
                concurrent enemies
              </text>
            </g>

            {/* worst-second bands */}
            {SERIES.map((s) => {
              if (!s.band || !shown[s.key]) return null;
              const rows = DATA.filter((d) => d[s.lowKey] != null);
              if (rows.length < 2) return null;
              const d = mkBand
                .y0((r) => y(r[s.lowKey] as number))
                .y1((r) => y(r[s.key]))(rows);
              return (
                <path
                  key={`band-${s.key}`}
                  d={d ?? undefined}
                  fill={s.color}
                  opacity={drawn ? 0.11 : 0}
                  style={{ transition: "opacity .8s ease .5s" }}
                />
              );
            })}

            {/* series */}
            {SERIES.map((s, si) => {
              if (!shown[s.key]) return null;
              const d = mkLine.y((r) => y(r[s.key]))(DATA) ?? "";
              return (
                <g key={s.key}>
                  <path
                    d={d}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeDasharray={1400}
                    strokeDashoffset={drawn ? 0 : 1400}
                    style={{
                      transition: `stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1) ${si * 0.16}s`,
                    }}
                  />
                  {DATA.map((r, i) => (
                    <circle
                      key={r.enemies}
                      cx={x(r.enemies)}
                      cy={y(r[s.key])}
                      r={hover === i ? 4.5 : 3}
                      fill="rgb(var(--surface))"
                      stroke={s.color}
                      strokeWidth={2}
                      opacity={drawn ? 1 : 0}
                      style={{
                        transition: `opacity .4s ease ${0.7 + i * 0.05}s, r .15s ease`,
                      }}
                    />
                  ))}
                </g>
              );
            })}

            {/* crosshair */}
            {active && (
              <line
                x1={x(active.enemies)}
                x2={x(active.enemies)}
                y1={0}
                y2={innerH}
                stroke="rgb(var(--line) / 0.4)"
              />
            )}

            <rect
              width={innerW}
              height={innerH}
              fill="transparent"
              onPointerMove={onMove}
              onPointerLeave={() => setHover(null)}
              style={{ cursor: "crosshair" }}
            />
          </g>
        </svg>

        {/* tooltip */}
        {active && (
          <div
            className="pointer-events-none absolute top-4 z-10 min-w-[9.5rem] rounded-lg border bg-surface/95 px-3 py-2 shadow-lg backdrop-blur"
            style={{
              left: Math.min(
                Math.max(MARGIN.left + x(active.enemies) + 12, 8),
                Math.max(w - 168, 8),
              ),
            }}
          >
            <p className="font-mono text-2xs text-faint">
              {active.enemies} enemies
            </p>
            <ul className="mt-1.5 space-y-1">
              {SERIES.filter((s) => shown[s.key]).map((s) => (
                <li key={s.key} className="flex items-center gap-2 text-xs">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: s.color }}
                  />
                  <span className="font-mono tabular-nums">
                    {active[s.key].toFixed(1)}
                  </span>
                  <span className="text-faint">fps</span>
                  {active[s.lowKey] != null && (
                    <span className="ml-auto font-mono text-2xs text-faint">
                      min {active[s.lowKey]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* legend / toggles */}
      <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
        {SERIES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() =>
              setShown((prev) => ({ ...prev, [s.key]: !prev[s.key] }))
            }
            className="chip transition-opacity hover:opacity-100"
            style={{ opacity: shown[s.key] ? 1 : 0.42 }}
            aria-pressed={shown[s.key]}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                background:
                  s.key === "instanced"
                    ? "rgb(var(--accent))"
                    : "rgb(var(--signal))",
              }}
            />
            {s.label}
          </button>
        ))}
        <p className="ml-auto hidden text-2xs text-faint md:block">
          A render-path A/B on one machine, not a scale test. Shaded band is the
          worst single second in each bracket.
        </p>
      </div>
    </div>
  );
}
